import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { TextField } from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";

import { LIST_TEAMS, SEARCH_TEAMS } from "../../constants/endpoints";
import { MIN_SEARCH_KEY_LENGTH, TIME_OUT_REQUEST } from "../../constants/data";
import { buildApiUrl, requestJson } from "../../dataProvider/requestJson";

const SEARCH_LIMIT = 20;

const toArray = (payload) => {
  if (Array.isArray(payload)) return payload;
  return payload?.teamsDto || payload?.dtos || payload?.content || [];
};

// City/state and team type tell apart teams that share a name.
export const toTeamOption = (team) => {
  const id = Number(team?.id ?? team?.teamId);
  if (!Number.isFinite(id) || id <= 0) return null;
  const name = String(team?.name ?? team?.teamName ?? "").trim();
  const ci = team?.contactInformation || {};
  const place = [ci.city, typeof ci.state === "string" ? ci.state.trim() : ""]
    .filter(Boolean)
    .join(", ");
  const detail = [place, team?.teamType].filter(Boolean).join(" · ");
  return {
    id,
    label: name || `Team #${id}`,
    detail: detail ? `#${id} · ${detail}` : `#${id}`,
  };
};

/**
 * Search-as-you-type team picker. Controlled: `value` is `{ id, label }` or null, and
 * `onChange` receives the picked option (or null when cleared).
 */
const TeamSearchField = ({
  value,
  onChange,
  onBlur,
  label,
  required,
  error,
  helperText,
  size,
  teamType,
}) => {
  const [options, setOptions] = useState([]);
  const [inputValue, setInputValue] = useState(value?.label || "");
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const timeoutRef = useRef(null);
  const requestIdRef = useRef(0);

  // Keep the text in sync when the selection changes from outside (e.g. form reset).
  useEffect(() => {
    setInputValue(value?.label || "");
  }, [value?.id, value?.label]);

  useEffect(() => () => clearTimeout(timeoutRef.current), []);

  const runSearch = async (query) => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setLoading(true);
    try {
      const params = { page: 0, limit: SEARCH_LIMIT, sort: "TeamName", teamType };
      const payload = await requestJson(
        buildApiUrl(query ? SEARCH_TEAMS({ ...params, query }) : LIST_TEAMS(params)),
      );
      if (requestId !== requestIdRef.current) return;
      setOptions(toArray(payload).map(toTeamOption).filter(Boolean));
    } catch (_) {
      if (requestId !== requestIdRef.current) return;
      setOptions([]);
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  };

  const scheduleSearch = (query) => {
    clearTimeout(timeoutRef.current);
    const trimmed = query.trim();
    if (trimmed.length > 0 && trimmed.length < MIN_SEARCH_KEY_LENGTH) {
      requestIdRef.current += 1;
      setOptions([]);
      setLoading(false);
      return;
    }
    timeoutRef.current = setTimeout(() => runSearch(trimmed), TIME_OUT_REQUEST);
  };

  const handleOpen = () => {
    setOpen(true);
    // Typing the selected team's name back in would only find that one team.
    if (!options.length) runSearch(value ? "" : inputValue.trim());
  };

  const handleInputChange = (_, next, reason) => {
    const text = String(next || "");
    setInputValue(text);
    if (reason === "input") scheduleSearch(text);
    if (reason === "clear") scheduleSearch("");
  };

  // Keep the current selection in the list so MUI can match it while results change.
  const displayOptions =
    value && !options.some((o) => o.id === value.id) ? [value, ...options] : options;

  const noOptionsText =
    inputValue.trim().length > 0 && inputValue.trim().length < MIN_SEARCH_KEY_LENGTH
      ? `Type at least ${MIN_SEARCH_KEY_LENGTH} characters`
      : "No teams found";

  return (
    <Autocomplete
      open={open}
      onOpen={handleOpen}
      onClose={() => setOpen(false)}
      options={displayOptions}
      value={value || null}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      onChange={(_, next) => onChange(next || null)}
      onBlur={onBlur}
      loading={loading}
      filterOptions={(x) => x}
      getOptionLabel={(option) => option?.label || ""}
      getOptionSelected={(option, selected) => option?.id === selected?.id}
      noOptionsText={noOptionsText}
      renderOption={(option) => (
        <div>
          <div>{option.label}</div>
          {option.detail && (
            <div style={{ fontSize: "12px", color: "#667085" }}>{option.detail}</div>
          )}
        </div>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          variant="outlined"
          size={size}
          required={required}
          error={error}
          helperText={helperText || (value ? `Team ID ${value.id}` : "Search by team name")}
        />
      )}
    />
  );
};

TeamSearchField.propTypes = {
  value: PropTypes.shape({
    id: PropTypes.number,
    label: PropTypes.string,
    detail: PropTypes.string,
  }),
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func,
  label: PropTypes.string,
  required: PropTypes.bool,
  error: PropTypes.bool,
  helperText: PropTypes.string,
  size: PropTypes.string,
  teamType: PropTypes.string,
};

TeamSearchField.defaultProps = {
  value: null,
  label: "Team",
  size: "small",
};

export default TeamSearchField;
